/**
 * AP-0/AP-3/AP-6: Einstiegspunkt + provisorische Vorschau-Shell.
 *
 * Wegwerfcode: die Shell existiert nur, damit die Basis-Controls und die vier
 * Themes (AP-0), der Empfangsweg SLCAN -> Worker -> UI (AP-3) sowie die 5
 * Toolbar-Menüs/Filterdialoge/Settings-Persistenz (AP-6) sichtbar prüfbar
 * sind. Die echte Anwendungs-Shell entsteht in AP-12 unter src/app/AppShell.ts
 * und ersetzt diese Datei dann.
 */

import './ui/tokens.css';
import './main.css';
import {
  createButton,
  createCheckbox,
  createCombo,
  createLabel,
  setButtonOverride,
} from './ui/controls';
import { getTheme, initTheme, onThemeChanged, themeDisplayName } from './ui/theme';
import { busStateColor } from './ui/busStateColor';
import { registerServiceWorker } from './pwa';
import { createAppShell } from './app/AppShell';
import { CanConnection } from './app/connection';
import { onFilesLaunched, readTraceText } from './app/files';
import { AppStore } from './app/store';
import { packFrames, parseTrace, traceFormatOf } from './trace';
import {
  createDecodingMenuButton,
  createFilterMenuButton,
  createHelpMenuButton,
  createRecorderMenuButton,
  createViewMenuButton,
} from './app/menus';
import { BITRATE_COMBO_KBPS, DEFAULT_BITRATE, type CanBitrate, type PortRef } from './can/ICanAdapter';
import { DEFAULT_BRIDGE_URL } from './can/BridgeAdapter';
import {
  CanvasTable,
  FrameTable,
  TABLE_COLUMN_COUNT,
  defaultColumnWidths,
  describeColumnWidth,
  type SignalDetailLine,
} from './ui/CanvasTable';
import {
  createDbcBrowser,
  createEdsBrowser,
  createSignalDetail,
  type DbcSnapshot,
  type EdsSnapshot,
  type SignalDetailHandle,
} from './views';
import {
  decodeSignalValue,
  findDbcMessage,
  formatSignalDisplay,
  maxSpnDigits,
  signalDetailKey,
  signalInfoText,
} from './views/signalValue';
import { mountViewsB } from './views/viewsB';
import { mountViewsC } from './views/viewsC';
import { mountViewsD } from './views/viewsD';
import { mountLicense, promptLicenseRequired } from './views/License';
import { licenseManager } from './license';
import { frameCount, readFrameRecord, type BridgeTarget, type CanBackend, type ConnectionState } from './worker/protocol';

const connection = new CanConnection();
const store = new AppStore(connection);
let selectedBitrate: CanBitrate = DEFAULT_BITRATE;
let currentPort: PortRef | null = null;

// AP-15: Brücke (PEAK/Sontheim). Adresse und Token überleben den Reload, damit
// nicht bei jedem Start neu getippt werden muss.
const BRIDGE_URL_KEY = 'cm.bridge.url';
const BRIDGE_TOKEN_KEY = 'cm.bridge.token';
let selectedInterface: CanBackend = 'slcan';
let bridgePorts: PortRef[] = [];
let bridgePortIndex = 0;

/** `null`, wenn kein Token vorliegt und der Nutzer die Abfrage abbricht. */
function bridgeTarget(): BridgeTarget | null {
  const url = localStorage.getItem(BRIDGE_URL_KEY) ?? DEFAULT_BRIDGE_URL;
  let token = localStorage.getItem(BRIDGE_TOKEN_KEY) ?? '';
  if (token === '') {
    token = window.prompt(`Token von can_bridge (${url}):`, '') ?? '';
    if (token === '') return null;
    localStorage.setItem(BRIDGE_TOKEN_KEY, token);
  }
  localStorage.setItem(BRIDGE_URL_KEY, url);
  return { url, token };
}

/** Beim Start automatisch geladene DBC (Ordner `DBC/` im Projektstamm). */
const DEFAULT_DBC_NAME = 'j1939_Github.dbc';
const DEFAULT_DBC_URL = `${import.meta.env.BASE_URL}DBC/${DEFAULT_DBC_NAME}`;

/**
 * `DecodingActive()` des Originals: ohne jede Dekodierquelle sind
 * "bekannt"/"unbekannt" bedeutungslos, dann bleiben die Zeilen ungefärbt.
 */
let dbcLoaded = false;
let edsLoaded = false;
function decodingActive(): boolean {
  return dbcLoaded || edsLoaded || store.decodeFlags.isobusEnabled || store.decodeFlags.canopenEnabled;
}

// --- Views A (AP-7) --------------------------------------------------------

const dbcBrowser = createDbcBrowser({
  loadSnapshot: () => connection.getSnapshot('dbc') as Promise<DbcSnapshot>,
});

const edsBrowser = createEdsBrowser({
  loadSnapshot: () => connection.getSnapshot('eds') as Promise<EdsSnapshot>,
  sendFrame: (frame) => connection.sendFrame({ id: frame.id, dlc: frame.dlc, data: frame.data }),
});

/** Je Botschaft (PGN bzw. 11-Bit-ID) ein eigenes Detailfenster (FM-WIN-03). */
const signalDetails = new Map<number, SignalDetailHandle>();

/**
 * Letzter DBC-Schnappschuss. Die aufgeklappte Signalliste (Help > Signal
 * Detail > List) wird beim Zeichnen gebraucht und kann nicht warten.
 */
let dbcSnapshot: DbcSnapshot | null = null;

/** Von main() gesetzt: passt die Description-Spalte an den neuen Katalog an. */
let fitDescriptionColumn: (() => void) | null = null;

function refreshDbcSnapshot(): void {
  void connection.getSnapshot('dbc').then(
    (snapshot) => {
      dbcSnapshot = snapshot as DbcSnapshot;
      fitDescriptionColumn?.();
    },
    () => {
      dbcSnapshot = null;
    },
  );
}

/** Signalzeilen der Botschaft einer Tabellenzeile, mit den Werten dieser Zeile. */
function signalDetailLines(id: number, data: Uint8Array, dlc: number): SignalDetailLine[] {
  const message = findDbcMessage(dbcSnapshot?.messages, id);
  if (message === undefined) return [];
  const shown = Math.min(dlc, 8);
  const spnDigits = maxSpnDigits(message);
  return message.signals.map((sig) => ({
    name: sig.name,
    value: formatSignalDisplay(sig, decodeSignalValue(sig, data, shown)),
    info: signalInfoText(sig, spnDigits),
  }));
}

async function openSignalDetail(frameId: number, data: Uint8Array, dlc: number): Promise<void> {
  const key = signalDetailKey(frameId);
  const existing = signalDetails.get(key);
  if (existing !== undefined) {
    if (dlc > 0) existing.updateFrame(data, dlc);
    existing.show();
    return;
  }
  // Das Fenster oeffnet nur, solange DBC-Dekodierung greift — ohne passende
  // Botschaft gaebe es weder Titel noch Signalliste.
  const snapshot = (await connection.getSnapshot('dbc')) as DbcSnapshot;
  const message = findDbcMessage(snapshot.messages, frameId);
  if (message === undefined) return;
  const detail = createSignalDetail(message);
  signalDetails.set(key, detail);
  // Sofort mit der angeklickten Zeile fuellen, sonst bleibt das Fenster leer,
  // bis der naechste Frame kommt (bei gestopptem Capture: fuer immer).
  if (dlc > 0) detail.updateFrame(data, dlc);
  detail.show();
}

function createSpacer(): HTMLElement {
  const spacer = document.createElement('div');
  spacer.className = 'cm-toolbar__spacer';
  return spacer;
}

/** Zwei Zeilen wie im Original: Verbindung oben, Menues + Capture/Clear darunter. */
function buildToolbar(
  connectButton: HTMLButtonElement,
  interfaceControls: readonly HTMLElement[],
  captureElement: HTMLElement,
): HTMLElement {
  const toolbar = document.createElement('div');
  toolbar.className = 'cm-toolbar';

  const connectionRow = document.createElement('div');
  connectionRow.className = 'cm-toolbar__row';
  connectionRow.append(
    connectButton,
    createLabel('Interface:'),
    ...interfaceControls,
    createLabel('Baudrate:'),
    createCombo({
      items: BITRATE_COMBO_KBPS.map((kbit) => ({ value: String(kbit), label: `${kbit} kBit/s` })),
      value: String(DEFAULT_BITRATE / 1000),
      onChange: (value) => {
        selectedBitrate = (Number(value) * 1000) as CanBitrate;
      },
    }),
  );

  const menuRow = document.createElement('div');
  menuRow.className = 'cm-toolbar__row';
  menuRow.append(
    createDecodingMenuButton(store),
    createViewMenuButton(store),
    createFilterMenuButton(store),
    createHelpMenuButton(store),
    createSpacer(),
    // Original: `captureBtn_` (Stop/Start) sitzt links neben `Clear`, beide rechtsbuendig.
    captureElement,
    createButton('Clear', { onClick: () => store.clearTable() }),
  );

  toolbar.append(connectionRow, menuRow);
  return toolbar;
}

/** Tab-Leiste des Originals ueber der Statuszeile (LayoutControls, tabStripY). */
function buildTabStrip(): HTMLElement {
  const row = document.createElement('div');
  row.className = 'cm-tabstrip';

  row.append(
    // FM-WIN-12: immer eigenes Fenster, nie eingebettet.
    createButton('Send Frame', { onClick: () => store.openView('tx-message', false) }),
    createRecorderMenuButton(store),
    createSpacer(),
    // FM-UI-08: Bus Stats haengt am rechten Rand dieser Zeile.
    createButton('Bus Stats', { onClick: () => store.openView('bus-stats', store.settings.singleWindowMode) }),
  );

  return row;
}

function mount(root: HTMLElement): void {
  const status = document.createElement('div');
  status.className = 'cm-status';
  const statusText = document.createElement('span');
  const statsText = document.createElement('span');
  statsText.style.marginLeft = 'auto';
  // Eigenes Element, damit nur der Zustand seine Farbe bekommt.
  const statsHeadText = document.createElement('span');
  const statsStateText = document.createElement('span');
  const statsTailText = document.createElement('span');
  statsText.append(statsHeadText, statsStateText, statsTailText);
  status.append(statusText, statsText);
  const setStatus = (text: string): void => {
    statusText.textContent = text;
  };

  const frameTable = new FrameTable({ viewMode: store.settings.viewMode });
  const tableView = new CanvasTable({
    source: frameTable,
    rxFadeMs: store.settings.rxFadeMs,
    rxColorMode: store.settings.rxColorMode,
    decodingActive: decodingActive(),
    columnWidths: store.settings.columnWidths,
    onActivate: (key) => store.emit('frame:activated', key),
    onColumnWidthsChanged: (widths) => store.setColumnWidths(widths),
    signalDetailMode: store.settings.signalDetailMode,
    detailLines: (key) => signalDetailLines(key.id, key.data, key.dlc),
  });

  // ComputeDescriptionColumnWidth(): die Spalte fasst den laengsten Text, den
  // die geladene DBC erzeugen kann. Selbst gezogene Breiten haben Vorrang.
  fitDescriptionColumn = () => {
    if (store.settings.columnWidths.length === TABLE_COLUMN_COUNT) return;
    const texts = (dbcSnapshot?.messages ?? []).map((message) =>
      message.comment !== ''
        ? message.comment
        : message.signals.map((sig) => `${sig.name}=999999.99`).join(' | '),
    );
    const width = describeColumnWidth(texts, tableView.monospaceCharWidth, tableView.widthMetrics);
    tableView.setColumnWidths(defaultColumnWidths(tableView.widthMetrics, width));
  };

  const capture = createCheckbox('Capture', {
    checked: true,
    onChange: (checked) => {
      void connection.setCapture(checked);
      setStatus(
        checked ? 'Capture resumed' : 'Capture stopped (frames still received, display frozen)',
      );
    },
  });
  const tabStrip = buildTabStrip();

  // AP-12: der Split-Bereich gehoert jetzt der AppShell, damit die untere
  // Flaeche im Single-Window-Mode eine View aufnehmen kann.
  const shell = createAppShell(store, tableView.element);

  // --- Interface-Auswahl (AP-15: SLCAN oder Bruecke) -----------------------

  const channelLabel = createLabel('Channel:');
  const channelCombo = createCombo({
    items: [],
    title: 'Kanaele, die can_bridge meldet',
    onChange: (value) => {
      bridgePortIndex = Number(value);
    },
  });

  const fillChannelCombo = (): void => {
    channelCombo.replaceChildren();
    for (const [index, port] of bridgePorts.entries()) {
      const option = document.createElement('option');
      option.value = String(index);
      option.textContent = port.label;
      channelCombo.append(option);
    }
    bridgePortIndex = bridgePorts.length > 0 ? 0 : -1;
    channelCombo.disabled = bridgePorts.length === 0;
  };

  // `hidden` reicht nicht: `.cm-label` setzt ein eigenes `display`.
  const showBridgeControls = (visible: boolean): void => {
    channelLabel.style.display = visible ? '' : 'none';
    channelCombo.style.display = visible ? '' : 'none';
  };
  showBridgeControls(false);

  /** Kandidaten neu holen; ein abgelehntes Token wird verworfen, damit man es korrigieren kann. */
  const refreshBridgePorts = async (): Promise<void> => {
    const target = bridgeTarget();
    if (target === null) {
      setStatus('Bridge: kein Token angegeben');
      return;
    }
    try {
      await connection.ready();
      bridgePorts = await connection.listBridgePorts(target);
      fillChannelCombo();
      setStatus(`Bridge ${target.url}: ${bridgePorts.length} Kanaele`);
    } catch (error) {
      bridgePorts = [];
      fillChannelCombo();
      localStorage.removeItem(BRIDGE_TOKEN_KEY);
      setStatus(`Bridge: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const interfaceCombo = createCombo<CanBackend>({
    items: [
      { value: 'slcan', label: 'CANable / SLCAN' },
      { value: 'bridge', label: 'Bridge (PEAK/Sontheim)' },
    ],
    onChange: (value) => {
      selectedInterface = value;
      showBridgeControls(value === 'bridge');
      if (value === 'bridge') void refreshBridgePorts();
    },
  });

  const onConnect = (): void => {
    // Harte Sperre vor dem Lizenz-Check (CanMonitorApp.cpp:1893).
    if (store.settings.simulationMode) {
      setStatus('Connect blocked: disable Simulation Mode first');
      return;
    }
    // FM-CON-04: ohne gueltige Lizenz/Trial wird gar nicht erst geoeffnet
    // (CanMonitorApp.cpp:1899).
    if (!licenseManager.isUsageAllowed()) {
      promptLicenseRequired(store);
      return;
    }
    void (async () => {
      try {
        await connection.ready();
        if (selectedInterface === 'bridge') {
          const target = bridgeTarget();
          if (target === null) throw new Error('Bridge: kein Token angegeben');
          if (bridgePorts.length === 0) {
            bridgePorts = await connection.listBridgePorts(target);
            fillChannelCombo();
          }
          const port = bridgePorts[bridgePortIndex];
          if (port === undefined) throw new Error('Bridge: kein Kanal ausgewaehlt');
          await connection.open(port, selectedBitrate, 'bridge', target);
          return;
        }
        currentPort ??= await connection.pickPort();
        await connection.open(currentPort, selectedBitrate);
      } catch (error) {
        currentPort = null;
        // Der Worker meldet ein gescheitertes `open` nicht -- sonst bliebe die
        // Taste auf "Connecting..." stehen.
        applyConnectionState('disconnected');
        setStatus(`Connect failed: ${error instanceof Error ? error.message : String(error)}`);
      }
    })();
  };

  const onDisconnect = (): void => {
    void connection.close().then(
      () => setStatus('Disconnected'),
      (error: unknown) => setStatus(error instanceof Error ? error.message : String(error)),
    );
  };

  // FM-CON-02: ein Umschalter statt zweier Tasten, Beschriftung folgt dem Zustand.
  let connectionState: ConnectionState = 'disconnected';
  const connectButton = createButton('Connect', {
    accent: true,
    onClick: () => {
      if (connectionState === 'connected') onDisconnect();
      else onConnect();
    },
  });
  const applyConnectionState = (state: ConnectionState): void => {
    connectionState = state;
    const connected = state === 'connected';
    connectButton.textContent =
      connected ? 'Disconnect' : state === 'connecting' ? 'Connecting…' : 'Connect';
    connectButton.disabled = state === 'connecting';
    connectButton.classList.toggle('cm-btn--accent', !connected);
    setButtonOverride(connectButton, connected ? '#b3352f' : null);
  };

  root.append(
    buildToolbar(connectButton, [interfaceCombo, channelLabel, channelCombo], capture.element),
    shell.element,
    tabStrip,
    status,
  );
  // Erste Zeichnung anstossen, sobald die Tabelle im Layout haengt.
  tableView.invalidate();

  connection.on('connection', (event) => {
    applyConnectionState(event.state);
    setStatus(event.message);
  });
  connection.on('error', (event) => setStatus(`Fehler: ${event.message}`));
  store.on('status:changed', (event) => setStatus(event.text));

  connection.on('rx-batch', (event) => {
    const count = Math.min(event.count, frameCount(event.frames));
    const now = performance.now();
    for (let i = 0; i < count; i += 1) {
      const frame = readFrameRecord(event.frames, i);
      frameTable.append(frame, event.decoded[i] ?? null, now);
      // Views, die einzelne Frames brauchen: SDO-Antworten und Signal-Detail.
      edsBrowser.onFrame(frame.id, frame.data, frame.dlc);
      signalDetails.get(signalDetailKey(frame.id))?.updateFrame(frame.data, frame.dlc);
    }
    // Ein Repaint je Batch, nicht je Frame.
    tableView.invalidate();
    store.emit('frames:appended', { count, lastSeq: frameTable.rowCount() });
  });

  store.on('view:opened', (event) => {
    if (event.kind === 'dbc-browser') dbcBrowser.show();
    else if (event.kind === 'eds-browser') edsBrowser.show();
  });

  // Views B (AP-8): DM1-Monitor, Address Claim, Node Scan.
  mountViewsB(store, connection);

  // Views C (AP-9): Bus Statistics, Signal Trace / Plot.
  mountViewsC(store, connection);

  // Views D (AP-10): TX Message, Engine Simulator, About, Protokoll-Details.
  mountViewsD(store, connection);

  // AP-13: Lizenzpruefung, Hintergrund-Revalidierung, Sperre bei Ablauf.
  void mountLicense(store, connection);

  store.on('frame:activated', (event) => {
    void openSignalDetail(event.id, event.data, event.dlc);
  });

  store.on('viewmode:changed', (event) => {
    frameTable.setViewMode(event.mode);
    // Die Zeilenkennungen der beiden Modelle sind nicht dieselben.
    tableView.collapseDetails();
    tableView.invalidate();
  });

  store.on('frames:cleared', () => {
    frameTable.clear();
    tableView.collapseDetails();
    tableView.invalidate();
  });

  store.on('decode:changed', (event) => {
    dbcLoaded = event.dbcLoaded;
    edsLoaded = event.edsLoaded;
    refreshDbcSnapshot();
    tableView.setColorSettings({ decodingActive: decodingActive() });
    // Die Browser sind nicht live; nach dem Laden einer Datei einmal nachziehen.
    if (dbcBrowser.panel.visible) void dbcBrowser.refresh();
    if (edsBrowser.panel.visible) void edsBrowser.refresh();
  });

  store.on('settings:changed', (event) => {
    if (event.changed.includes('rxFadeMs') || event.changed.includes('rxColorMode')) {
      tableView.setColorSettings({
        rxFadeMs: event.settings.rxFadeMs,
        rxColorMode: event.settings.rxColorMode,
      });
    }
    if (event.changed.includes('signalDetailMode')) {
      tableView.setSignalDetailMode(event.settings.signalDetailMode);
    }
  });

  connection.on('stats', (event) => {
    const { stats } = event;
    const state = stats.busStatus.stateAvailable ? stats.busStatus.state : 'N/A';
    const errors = stats.busStatus.countersAvailable
      ? `${stats.busStatus.rxErrorCount}/${stats.busStatus.txErrorCount}`
      : 'N/A';
    const dropped = stats.droppedFrameCount > 0 ? `  |  Dropped: ${stats.droppedFrameCount}` : '';
    statsHeadText.textContent = `Rx: ${stats.rxFrameCount}  |  Bus State: `;
    statsStateText.textContent = state;
    statsStateText.style.color = busStateColor(state, stats.busStatus.stateAvailable) ?? '';
    statsTailText.textContent =
      `  |  Err Rx/Tx: ${errors}  |  Load: ${stats.busLoadPercent.toFixed(1)}%${dropped}`;
  });

  setStatus(`Bereit \u2014 Theme: ${themeDisplayName(getTheme())}`);
  onThemeChanged((theme) => {
    document.title = `CAN Monitor \u2014 ${themeDisplayName(theme)}`;
  });

  // AP-11: per `file_handlers` geöffnete Dateien (Doppelklick im Explorer).
  // Eine Trace wird nur geladen, nicht gestartet — ungefragt Frames auf einen
  // Bus zu schicken wäre die falsche Voreinstellung.
  onFilesLaunched((file) => {
    void (async () => {
      try {
        if (file.kind === 'trace') {
          const frames = parseTrace(await readTraceText(file.handle), traceFormatOf(file.name));
          await connection.playbackLoad(packFrames(frames));
          setStatus(`Playback file loaded: ${file.name} (${frames.length} frames)`);
          return;
        }
        const text = await (await file.handle.getFile()).text();
        if (file.kind === 'dbc') await store.loadDbc(file.name, text);
        else await store.loadEds(file.name, text);
      } catch (error) {
        setStatus(error instanceof Error ? error.message : String(error));
      }
    })();
  });

  void loadDefaultDbc(setStatus);
}

/** Lädt die mitgelieferte J1939-DBC, damit die Spalten Name/Description sofort gefüllt sind. */
async function loadDefaultDbc(setStatus: (text: string) => void): Promise<void> {
  try {
    await connection.ready();
    const response = await fetch(DEFAULT_DBC_URL);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    // DBC-Dateien sind Latin-1; `Response.text()` würde UTF-8 annehmen.
    const text = new TextDecoder('windows-1252').decode(await response.arrayBuffer());
    await store.loadDbc(DEFAULT_DBC_NAME, text);
  } catch (error) {
    setStatus(`Default DBC nicht geladen: ${error instanceof Error ? error.message : String(error)}`);
  }
}

initTheme(store.settings.theme, store.settings.uiScalePercent);
registerServiceWorker();

const appRoot = document.querySelector<HTMLElement>('#app');
if (appRoot) mount(appRoot);

